import {useMemo, useState} from 'react';

import {Box, ButtonBase, Stack, Typography} from '@mui/material';

import VarsPanel from '../../contentGenerator/components/VarsPanel';
import ExportMenu from '../../shared/ExportMenu';
import {useProductContext} from '../../shared/ProductContext';
import {analyzeListing} from '../../shared/seo/seoChecks';
import SeoScoreBadge from '../../shared/seo/SeoScoreBadge';
import ProductSelector from '../../shared/ProductSelector';

import {C, FILTER_OPTIONS} from '../constants';
import {buildSidebarGroups, getListingStats} from '../utils';
import {
    ContentLimitsPanel,
    CustomCommandsPanel,
    GroupManagerDialog,
} from './Panels';

function ListingRowItem({
    active,
    brandProfile,
    listing,
    onDelete,
    onSelect,
    publishValidation = null,
}) {
    const stats = getListingStats(listing, publishValidation);
    const seo = useMemo(() => analyzeListing(listing, [], brandProfile), [listing, brandProfile]);

    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect();
                }
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 11px',
                borderRadius: '10px',
                background: active ? `${C.teal}12` : 'transparent',
                border: `1px solid ${active ? `${C.teal}33` : 'transparent'}`,
                cursor: 'pointer',
                marginBottom: '6px',
            }}>
            <Box sx={{flex: 1, minWidth: 0}}>
                <Typography
                    sx={{
                        fontSize: 13,
                        fontWeight: active ? 700 : 600,
                        color: C.ink,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}>
                    {listing.name}
                </Typography>
                <Stack direction="row" gap="6px" sx={{marginTop: '3px', fontSize: 11, color: C.textSoft, flexWrap: 'wrap'}}>
                    {listing.sku && <Typography component="span" sx={{fontSize: 11}}>{listing.sku}</Typography>}
                    {listing.category && <Typography component="span" sx={{fontSize: 11}}>{listing.category}</Typography>}
                    <Typography component="span" sx={{fontSize: 11}}>
                        {stats.approved}/{stats.total}
                    </Typography>
                    {seo && <SeoScoreBadge grade={seo.grade} score={seo.score} size="small" />}
                </Stack>
            </Box>
            <ButtonBase
                onClick={(event) => {
                    event.stopPropagation();
                    onDelete();
                }}
                aria-label={`Delete ${listing.name}`}
                sx={{
                    border: `1px solid ${C.border}`,
                    background: 'transparent',
                    color: C.textSoft,
                    borderRadius: '8px',
                    width: 26,
                    height: 26,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 16,
                    lineHeight: 1,
                }}>
                ×
            </ButtonBase>
        </Box>
    );
}

function GroupBlock({
    activeId,
    allListings,
    brandProfile,
    groupKey,
    listings,
    onDeleteListing,
    onSelect,
    onSetSuperSku,
    publishValidationByListingId = {},
}) {
    const [collapsed, setCollapsed] = useState(false);
    const [showManager, setShowManager] = useState(false);
    const approved = listings.reduce(
        (sum, listing) =>
            sum +
            getListingStats(
                listing,
                publishValidationByListingId?.[listing.id] || null,
            ).approved,
        0,
    );
    const total = listings.reduce((sum, listing) => sum + getListingStats(listing).total, 0);

    return (
        <Box sx={{marginBottom: '10px'}}>
            <Stack
                direction="row"
                alignItems="center"
                gap="8px"
                sx={{
                    padding: '8px 10px',
                    borderRadius: '10px',
                    background: C.amberL,
                    border: `1px solid ${C.amber}33`,
                    marginBottom: collapsed ? 0 : '6px',
                }}>
                <ButtonBase
                    onClick={() => setCollapsed((current) => !current)}
                    aria-expanded={!collapsed}
                    aria-label={collapsed ? 'Expand group' : 'Collapse group'}
                    sx={{
                        border: 'none',
                        background: 'transparent',
                        color: C.amber,
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 13,
                        lineHeight: 1,
                        transition: 'transform .15s',
                        transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    }}>
                    ▾
                </ButtonBase>
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography sx={{fontSize: 11, fontWeight: 700, color: C.amber, fontFamily: 'monospace'}}>{groupKey}</Typography>
                    <Typography sx={{fontSize: 10, color: C.amber}}>{listings.length} variant(s)</Typography>
                </Box>
                <Typography component="span" sx={{fontSize: 10, color: C.amber}}>
                    {approved}/{total}
                </Typography>
                <ButtonBase
                    onClick={() => setShowManager(true)}
                    sx={{
                        border: `1px solid ${C.amber}44`,
                        background: C.surface,
                        color: C.amber,
                        borderRadius: 999,
                        padding: '4px 9px',
                        fontFamily: 'inherit',
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: 'pointer',
                    }}>
                    Manage
                </ButtonBase>
            </Stack>

            {!collapsed && (
                <Box sx={{paddingLeft: '10px', borderLeft: `2px solid ${C.amber}33`, marginLeft: '10px'}}>
                    {listings.map((listing) => (
                        <ListingRowItem
                            key={listing.id}
                            active={listing.id === activeId}
                            brandProfile={brandProfile}
                            listing={listing}
                            onDelete={() => onDeleteListing(listing.id)}
                            onSelect={() => onSelect(listing.id)}
                            publishValidation={
                                publishValidationByListingId?.[listing.id] || null
                            }
                        />
                    ))}
                </Box>
            )}

            {showManager && (
                <GroupManagerDialog
                    allListings={allListings}
                    groupKey={groupKey}
                    onClose={() => setShowManager(false)}
                    onSetSuperSku={onSetSuperSku}
                />
            )}
        </Box>
    );
}

export default function Sidebar({
    activeId,
    activeProfile,
    allListings,
    brandProfile = null,
    charLimits,
    customCommands,
    filterMode,
    filteredListings,
    onExportCsv,
    onDeleteListing,
    onExportApproved,
    onSelect,
    onSetCustomCommands,
    onSetFilterMode,
    onSetSuperSku,
    onShowConfigs,
    onShowImport,
    publishValidationByListingId = {},
    sidebarExpanded = false,
}) {
    const {vars, setVars} = useProductContext();
    const stats = useMemo(
        () =>
            allListings.reduce(
                (accumulator, listing) => {
                    const listingStats = getListingStats(
                        listing,
                        publishValidationByListingId?.[listing.id] || null,
                    );
                    return {
                        approved: accumulator.approved + listingStats.approved,
                        total: accumulator.total + listingStats.total,
                        flagged: accumulator.flagged + listingStats.flagged,
                        flaggedListings:
                            accumulator.flaggedListings + (listingStats.flagged > 0 ? 1 : 0),
                    };
                },
                {approved: 0, total: 0, flagged: 0, flaggedListings: 0},
            ),
        [allListings, publishValidationByListingId],
    );
    const sidebarGroups = useMemo(() => buildSidebarGroups(filteredListings), [filteredListings]);

    return (
        <Stack
            component="aside"
            direction="column"
            sx={{
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
                background: C.surface,
                borderRight: `1px solid ${C.border}`,
            }}>
            {/* ── Header ── */}
            <Box sx={{padding: '14px 14px 12px', borderBottom: `1px solid ${C.border}`}}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap="8px" sx={{marginBottom: '10px'}}>
                    <Typography sx={{fontFamily: "'Playfair Display',Georgia,serif", fontSize: 20, lineHeight: 1.1, color: C.ink}}>
                        Content Review
                    </Typography>
                    <Stack direction="row" alignItems="center" gap="6px">
                        <ButtonBase
                            onClick={onShowImport}
                            sx={{
                                border: 'none',
                                background: C.purple,
                                color: C.surface,
                                borderRadius: '8px',
                                padding: '6px 10px',
                                fontFamily: 'inherit',
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: 'pointer',
                            }}>
                            Import
                        </ButtonBase>
                        <ButtonBase
                            onClick={onShowConfigs}
                            sx={{
                                border: 'none',
                                background: C.teal,
                                color: C.surface,
                                borderRadius: '8px',
                                padding: '6px 10px',
                                fontFamily: 'inherit',
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: 'pointer',
                            }}>
                            Configs
                        </ButtonBase>
                    </Stack>
                </Stack>

                {/* ── Product Selector at the top ── */}
                <ProductSelector />
            </Box>

            {/* ── Product variables (sits under the selector so the
                 user can scan/adjust resolved values right next to the
                 product chips) ── */}
            <VarsPanel vars={vars} setVars={setVars} />

            {/* ── Filter + listings ── */}
            <Box sx={{display: 'flex', gap: '4px', padding: '8px 12px', borderBottom: `1px solid ${C.border}`, background: C.surfaceAlt, flexWrap: 'wrap'}}>
                {FILTER_OPTIONS.map((option) => {
                    const isActive = filterMode === option.key;
                    const isFlaggedTab = option.key === 'flagged';
                    const hasFlagged = isFlaggedTab && stats.flagged > 0;
                    let background = isActive ? C.ink : 'transparent';
                    let color = isActive ? C.surface : C.textSoft;
                    let border = 'none';

                    if (hasFlagged && !isActive) {
                        background = C.coralL;
                        color = C.coral;
                        border = `1px solid ${C.coral}66`;
                    } else if (hasFlagged && isActive) {
                        background = C.coral;
                        color = C.surface;
                    }

                    return (
                        <ButtonBase
                            key={option.key}
                            onClick={() => onSetFilterMode(option.key)}
                            sx={{
                                border,
                                background,
                                color,
                                borderRadius: 999,
                                padding: '4px 10px',
                                fontFamily: 'inherit',
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: 'pointer',
                            }}>
                            {hasFlagged ? `${option.label} (${stats.flagged})` : option.label}
                        </ButtonBase>
                    );
                })}
                <Typography component="span" sx={{marginLeft: 'auto', fontSize: 10, color: C.textSoft, alignSelf: 'center'}}>
                    {stats.approved}/{stats.total}
                </Typography>
            </Box>

            <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px'}}>
                {!sidebarGroups.length && <Typography sx={{fontSize: 12, color: C.textSoft, textAlign: 'center', padding: '2rem 1rem'}}>No listings match.</Typography>}
                {sidebarGroups.map((group) =>
                    group.type === 'group' ? (
                        <GroupBlock
                            key={group.key}
                            activeId={activeId}
                            allListings={allListings}
                            brandProfile={brandProfile}
                            groupKey={group.key}
                            listings={group.listings}
                            onDeleteListing={onDeleteListing}
                            onSelect={onSelect}
                            onSetSuperSku={onSetSuperSku}
                            publishValidationByListingId={publishValidationByListingId}
                        />
                    ) : (
                        <ListingRowItem
                            key={group.listing.id}
                            active={group.listing.id === activeId}
                            brandProfile={brandProfile}
                            listing={group.listing}
                            onDelete={() => onDeleteListing(group.listing.id)}
                            onSelect={() => onSelect(group.listing.id)}
                            publishValidation={
                                publishValidationByListingId?.[group.listing.id] || null
                            }
                        />
                    ),
                )}
            </Box>

            <Box sx={{marginTop: 'auto', borderTop: `1px solid ${C.border}`, background: C.surface}}>
                <CustomCommandsPanel
                    commands={customCommands}
                    setCommands={onSetCustomCommands}
                    vars={vars}
                />
                <ContentLimitsPanel limits={charLimits} profile={activeProfile} />

                <ExportMenu
                    onExport={(format, scope) =>
                        scope === 'approved' ? onExportApproved(format) : onExportCsv(format)
                    }
                />
            </Box>
        </Stack>
    );
}
